import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Her supplements -------------------------------------------------

const coq10: ContentBlock<"supplement"> = {
  id: makeId("supplement-coq10"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "CoQ10 (Ubiquinol)",
  icon: "💊",
  body: {
    name: "CoQ10 / Ubiquinol",
    dosageRange: "400-600mg daily",
    dailyTiming:
      "Take with a meal that contains fat. Split higher doses between morning and evening if needed for tolerance.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "Ubiquinol is the reduced form and is often marketed as easier to absorb than ubiquinone, especially at higher doses.",
      absorption:
        "CoQ10 is fat-soluble, so absorption is better with a meal that contains fat.",
      timing:
        "Split higher daily doses if they upset your stomach or if your clinician wants steadier intake.",
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Organ meats", "Sardines", "Broccoli", "Cauliflower"],
    expectations: {
      canExpect: [
        "Supports mitochondrial energy in developing eggs",
        "May improve egg quality and embryo development",
        "Well-tolerated with minimal side effects",
      ],
      shouldNotExpect: [
        "Allow 3 months. It takes a full egg maturation cycle to see effects",
        "It won't increase your egg count. It supports the quality of eggs you have",
        "This supports, but doesn't replace, medical fertility treatment",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Ubiquinol",
        url: "https://www.amazon.com/s?k=ubiquinol+400mg",
      },
      { label: "iHerb - CoQ10", url: "https://www.iherb.com/c/coq10" },
    ],
  },
  sources: [
    {
      title:
        "Pretreatment with coenzyme Q10 improves ovarian response and embryo quality in low-prognosis young women with decreased ovarian reserve: a randomized controlled trial",
      authors: "Xu Y et al.",
      journal: "Reproductive Biology and Endocrinology",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/29587861/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Does coenzyme Q10 supplementation improve fertility outcomes in women undergoing assisted reproductive technology procedures? A systematic review and meta-analysis of randomized-controlled trials",
      authors: "Florou P et al.",
      journal: "Journal of Assisted Reproduction and Genetics",
      year: 2020,
      url: "https://link.springer.com/article/10.1007/s10815-020-01906-3",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const vitaminD: ContentBlock<"supplement"> = {
  id: makeId("supplement-vitamin-d"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Vitamin D",
  icon: "☀️",
  body: {
    name: "Vitamin D3",
    dosageRange: "2,000-4,000 IU daily",
    dailyTiming:
      "Take with a meal that contains fat. Morning or midday is easiest for most people, but consistency matters more than clock time.",
    timing: "Start immediately - most people are deficient",
    bioavailability: {
      form: "Vitamin D3 is generally preferred over D2 for raising blood 25(OH)D levels.",
      absorption:
        "It is fat-soluble and absorbs best with a meal that contains fat.",
      metabolism:
        "Blood levels, body weight, baseline deficiency, and some medications can change how much you need.",
      cautions: [
        "Avoid stacking high-dose vitamin D products without checking a blood level.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "both",
    foods: ["Fatty fish", "Egg yolks", "Fortified milk", "Mushrooms"],
    expectations: {
      canExpect: [
        "Linked to better IVF outcomes and implantation rates",
        "Supports immune function during pregnancy",
        "Cheap and widely available",
      ],
      shouldNotExpect: [
        "It corrects deficiency, not low egg count. It is not an ovarian reserve treatment",
        "Test your baseline level first. Dosing depends on your current levels",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Vitamin D3",
        url: "https://www.amazon.com/s?k=vitamin+d3+4000+iu",
      },
      { label: "iHerb - Vitamin D", url: "https://www.iherb.com/c/vitamin-d" },
    ],
  },
  sources: [
    {
      title:
        "Vitamin D and assisted reproductive treatment outcome: a systematic review and meta-analysis",
      authors: "Chu J et al.",
      journal: "Human Reproduction",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/29149263/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Effect of Vitamin D Supplementation on In Vitro Fertilization Outcomes: A Trial Sequential Meta-Analysis of 5 Randomized Controlled Trials",
      authors: "Zhou X et al.",
      journal: "Frontiers in Endocrinology",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8969598/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const omega3: ContentBlock<"supplement"> = {
  id: makeId("supplement-omega-3"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Omega-3 (EPA/DHA)",
  icon: "🐟",
  body: {
    name: "Omega-3 fatty acids (EPA/DHA)",
    dosageRange: "1-2g EPA/DHA combined daily",
    dailyTiming:
      "Take with a meal that contains fat to improve absorption and reduce fishy burps.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "Triglyceride or re-esterified triglyceride fish oil is often better absorbed than ethyl ester forms.",
      absorption:
        "Take with a meal, ideally one that contains fat, to improve absorption and reduce fishy burps.",
      cautions: [
        "Check EPA/DHA amount, not just total fish oil, and avoid rancid products.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "both",
    foods: ["Salmon", "Sardines", "Walnuts", "Flaxseeds"],
    expectations: {
      canExpect: [
        "Anti-inflammatory effects supporting reproductive health",
        "May improve egg and sperm quality",
        "Benefits overall cardiovascular and brain health",
      ],
      shouldNotExpect: [
        "Unlikely to dramatically change fertility test numbers on its own",
        "Works best alongside a balanced diet, not as a replacement for one",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Omega-3",
        url: "https://www.amazon.com/s?k=omega+3+fish+oil+epa+dha",
      },
      {
        label: "iHerb - Omega-3",
        url: "https://www.iherb.com/c/omega-3-fish-oil",
      },
    ],
  },
  sources: [
    {
      title:
        "Effect of omega-3 supplements or diets on fertility in women: A meta-analysis",
      authors: "Trop-Steinberg S et al.",
      journal: "Heliyon",
      year: 2024,
      url: "https://pubmed.ncbi.nlm.nih.gov/38628754/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const methylfolate: ContentBlock<"supplement"> = {
  id: makeId("supplement-methylfolate"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "Methylfolate",
  icon: "🧬",
  body: {
    name: "Methylfolate (L-5-MTHF)",
    dosageRange: "800-1,000mcg daily",
    dailyTiming:
      "Take at the same time each day, with or without food. Morning dosing can be easier if B vitamins feel activating.",
    timing: "Start at least 1 month before conception attempt",
    bioavailability: {
      form: "L-5-MTHF is the active folate form and bypasses the folic-acid conversion step.",
      metabolism:
        "This can matter for people with reduced folate conversion, but it does not mean higher doses are automatically better.",
      cautions: [
        "Avoid doubling up high-dose folate across prenatal, B-complex, and standalone products unless your clinician wants that.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Leafy greens", "Legumes", "Citrus fruits", "Fortified grains"],
    expectations: {
      canExpect: [
        "Prevents neural tube defects - this is the primary reason to take it",
        "May bypass the conversion step for people with MTHFR variants (mechanistic; direct outcome data is limited)",
        "Supports DNA synthesis in rapidly dividing cells",
      ],
      shouldNotExpect: [
        "It won't improve egg count or quality. Its role is preventing neural tube defects",
        "If you already get enough folate from food, the added benefit is minimal",
        "Rigorous head-to-head outcome trials of L-5-MTHF vs folic acid in MTHFR carriers do not exist - the case for choosing methylfolate rests on mechanism, not on a clear live-birth benefit",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Methylfolate",
        url: "https://www.amazon.com/s?k=methylfolate+1000mcg",
      },
      { label: "iHerb - L-5-MTHF", url: "https://www.iherb.com/c/folic-acid" },
    ],
  },
  sources: [
    {
      title:
        "Effects and safety of periconceptional oral folate supplementation for preventing birth defects",
      authors: "De-Regil LM et al.",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2015,
      url: "https://pubmed.ncbi.nlm.nih.gov/26662928/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "MTHFR isoform carriers. 5-MTHF (5-methyl tetrahydrofolate) vs folic acid: a key to pregnancy outcome: a case series",
      authors: "Servy EJ, Jacquesson-Fournols L, Cohen M, Menezo YJR",
      journal: "Journal of Assisted Reproduction and Genetics",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/29882091/",
      evidenceLevel: "observational",
    },
  ],
};

const prenatal: ContentBlock<"supplement"> = {
  id: makeId("supplement-prenatal"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "Prenatal Vitamin",
  icon: "💊",
  body: {
    name: "Prenatal + Methylfolate",
    dosageRange: "1 daily - look for 800-1,000mcg methylfolate",
    dailyTiming:
      "Take with food if it causes nausea. Keep it at least 4 hours away from levothyroxine and separate it from tetracycline or quinolone antibiotics as directed.",
    timing: "Start at least 3 months before conception or fertility treatment",
    bioavailability: {
      form: "Look for a prenatal that clearly lists folate form and dose, iodine as a predictable form such as potassium iodide, and iron in a tolerable form if you need it.",
      absorption:
        "Iron, calcium, magnesium, and zinc can compete with each other and with some medications. Taking the prenatal with food can reduce nausea, but coffee, tea, calcium-heavy meals, and antacids may reduce iron absorption.",
      metabolism:
        "Methylfolate is already in an active folate form. That can help when folic-acid conversion is reduced, but it does not mean higher folate doses are always better.",
      timing:
        "If you take levothyroxine, take the prenatal at least 4 hours later. If you are on doxycycline or similar antibiotics, separate the prenatal from the antibiotic as your prescriber directs.",
      cautions: [
        "Check the full stack before adding separate iron, iodine, vitamin D, choline, omega-3, or methylfolate so you do not accidentally double-dose.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Leafy greens", "Legumes", "Fortified cereals", "Citrus fruits"],
    expectations: {
      canExpect: [
        "Prevents neural tube defects - critical in first 28 days post-conception",
        "Methylfolate form bypasses MTHFR gene variants (30-40% of women)",
        "Builds nutrient stores (iron, D3, DHA, choline, iodine, B12)",
      ],
      shouldNotExpect: [
        "Improved egg count or quality from the prenatal alone",
        "All prenatals are equal - look for third-party testing (NSF, USP)",
        "Benefits from folic acid if you have MTHFR variants",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Thorne Prenatal",
        url: "https://www.amazon.com/s?k=thorne+basic+prenatal",
      },
      {
        label: "Amazon - Ritual Prenatal",
        url: "https://www.amazon.com/s?k=ritual+prenatal+vitamin",
      },
    ],
  },
  sources: [
    {
      title:
        "Effects and safety of periconceptional oral folate supplementation for preventing birth defects",
      authors: "De-Regil LM et al.",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2015,
      url: "https://pubmed.ncbi.nlm.nih.gov/26662928/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const choline: ContentBlock<"supplement"> = {
  id: makeId("supplement-choline"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "Choline",
  icon: "🥚",
  body: {
    name: "Choline",
    dosageRange: "Aim for 450mg daily in pregnancy from food + supplements",
    dailyTiming:
      "Take supplemental choline with food, especially if it causes nausea or fishy burps.",
    timing:
      "Build intake before pregnancy and through all trimesters; the strongest outcome data is from third-trimester supplementation",
    bioavailability: {
      form: "Food choline, phosphatidylcholine, and choline salts can all contribute, but labels list different choline amounts.",
      absorption:
        "Take supplemental choline with food if it causes nausea or fishy burps.",
      metabolism:
        "Gut metabolism can influence fishy body odor at higher doses, so total intake matters.",
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Eggs", "Chicken", "Beef", "Milk", "Soybeans", "Peanuts"],
    expectations: {
      canExpect: [
        "Supports fetal brain and spinal cord development",
        "Helps close a common prenatal nutrition gap because many prenatals contain little or no choline",
        "Works best when food intake is counted too, since eggs and animal proteins can provide meaningful amounts",
      ],
      shouldNotExpect: [
        "It is not an egg-quality supplement or a treatment for infertility",
        "More is not automatically better - high doses can cause GI upset, sweating, or a fishy body odor",
        "It does not replace a prenatal vitamin with folate, iodine, iron, and vitamin D",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Choline",
        url: "https://www.amazon.com/s?k=choline+supplement+pregnancy",
      },
      {
        label: "iHerb - Choline",
        url: "https://www.iherb.com/search?kw=choline",
      },
    ],
  },
  sources: [
    {
      title: "Healthy Eating During Pregnancy",
      journal: "ACOG",
      year: 2025,
      url: "https://www.acog.org/womens-health/faqs/healthy-eating-during-pregnancy",
      evidenceLevel: "guideline",
    },
    {
      title: "Dietary Supplements and Life Stages: Pregnancy",
      journal: "NIH Office of Dietary Supplements",
      year: 2025,
      url: "https://ods.od.nih.gov/factsheets/Pregnancy-HealthProfessional/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Maternal choline supplementation during the third trimester of pregnancy improves infant information processing speed: a randomized, double-blind, controlled feeding study",
      authors: "Caudill MA et al.",
      journal: "The FASEB Journal",
      year: 2018,
      url: "https://faseb.onlinelibrary.wiley.com/doi/10.1096/fj.201700692RR",
      evidenceLevel: "rct",
    },
    {
      title:
        "Choline: Exploring the Growing Science on Its Benefits for Moms and Babies",
      authors: "Korsmo HW, Jiang X, Caudill MA",
      journal: "Nutrients",
      year: 2019,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6722688/",
      evidenceLevel: "review",
    },
  ],
};

const iodine: ContentBlock<"supplement"> = {
  id: makeId("supplement-iodine"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "thyroid"],
  title: "Iodine",
  icon: "🧂",
  body: {
    name: "Iodine",
    dosageRange:
      "150mcg daily in a prenatal supplement is commonly recommended; total pregnancy intake target is about 220mcg/day",
    dailyTiming:
      "Take with your prenatal or a meal. Avoid high-dose kelp products unless your clinician recommends them.",
    timing:
      "Start before conception if your prenatal does not already include iodine",
    bioavailability: {
      form: "Potassium iodide in a prenatal is more predictable than kelp or seaweed capsules.",
      absorption:
        "Iodine is usually well absorbed, but the dose in seaweed products can vary widely.",
      metabolism:
        "Your thyroid uses iodine to make thyroid hormone, so both too little and too much can matter.",
      cautions: [
        "Avoid high-dose kelp products if you have thyroid disease unless your clinician specifically recommends them.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Iodized salt", "Dairy", "Eggs", "Seafood", "Seaweed"],
    expectations: {
      canExpect: [
        "Supports thyroid hormone production during early pregnancy",
        "Helps meet fetal brain and nervous system iodine needs",
        "Flags an important prenatal-label check because not all prenatals contain iodine",
      ],
      shouldNotExpect: [
        "It will not treat thyroid disease on its own - abnormal TSH or thyroid antibodies need medical care",
        "Avoid high-dose kelp or seaweed supplements unless your clinician specifically recommends them",
        "If you already take levothyroxine or have thyroid disease, confirm dosing with your doctor",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Prenatal iodine",
        url: "https://www.amazon.com/s?k=prenatal+iodine+150mcg",
      },
      {
        label: "iHerb - Iodine",
        url: "https://www.iherb.com/search?kw=iodine%20150%20mcg",
      },
    ],
  },
  sources: [
    {
      title:
        "Iodine Supplementation for Pregnancy and Lactation—United States and Canada: Recommendations of the American Thyroid Association",
      authors: "Becker DV et al. (ATA Public Health Committee)",
      journal: "Thyroid",
      year: 2006,
      url: "https://pubmed.ncbi.nlm.nih.gov/17042677/",
      evidenceLevel: "expert-opinion",
    },
    {
      title: "Prepregnancy Counseling",
      journal: "ACOG",
      year: 2019,
      url: "https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2019/01/prepregnancy-counseling",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Systematic review and meta-analysis of the effects of iodine supplementation on thyroid function and child neurodevelopment in mildly-to-moderately iodine-deficient pregnant women",
      authors: "Dineva M et al.",
      journal: "American Journal of Clinical Nutrition",
      year: 2020,
      url: "https://pubmed.ncbi.nlm.nih.gov/32320029/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const calcium: ContentBlock<"supplement"> = {
  id: makeId("supplement-calcium"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Calcium",
  icon: "🦴",
  body: {
    name: "Calcium",
    dosageRange:
      "Aim for 1,000mg daily total from food + supplements; supplement only the gap",
    dailyTiming:
      "Take calcium carbonate with meals; calcium citrate can be taken with or without food. Split larger doses and separate from iron, thyroid medication, and some antibiotics.",
    timing:
      "Use when dietary intake is low, especially before pregnancy and during pregnancy",
    bioavailability: {
      form: "Calcium citrate can be taken with or without food; calcium carbonate absorbs best with meals.",
      absorption:
        "Absorption is better in smaller doses, so split larger supplemental amounts.",
      timing:
        "Separate calcium from iron, thyroid medication, and some antibiotics because it can reduce their absorption.",
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: [
      "Yogurt",
      "Milk",
      "Cheese",
      "Fortified plant milks",
      "Tofu",
      "Sardines",
    ],
    expectations: {
      canExpect: [
        "Supports maternal bone health and fetal bone and tooth development",
        "Helps complete a prenatal nutrition plan when diet is low in dairy or fortified foods",
        "Can be taken separately from prenatal vitamins if your prenatal does not contain enough calcium",
      ],
      shouldNotExpect: [
        "It does not improve egg count or egg quality",
        "Do not take calcium at the same time as iron if you are trying to improve iron absorption",
        "High supplemental doses can cause constipation or kidney stone risk in susceptible people",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Calcium citrate",
        url: "https://www.amazon.com/s?k=calcium+citrate+supplement",
      },
      {
        label: "iHerb - Calcium",
        url: "https://www.iherb.com/c/calcium",
      },
    ],
  },
  sources: [
    {
      title: "Healthy Eating During Pregnancy",
      journal: "ACOG",
      year: 2025,
      url: "https://www.acog.org/womens-health/faqs/healthy-eating-during-pregnancy",
      evidenceLevel: "guideline",
    },
    {
      title: "Dietary Supplements and Life Stages: Pregnancy",
      journal: "NIH Office of Dietary Supplements",
      year: 2025,
      url: "https://ods.od.nih.gov/factsheets/Pregnancy-HealthProfessional/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Calcium supplementation during pregnancy for preventing hypertensive disorders and related problems",
      authors: "Hofmeyr GJ, Lawrie TA, Atallah ÁN, Torloni MR",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/30277579/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

// --- His supplements -------------------------------------------------

const zinc: ContentBlock<"supplement"> = {
  id: makeId("supplement-zinc"),
  kind: "supplement",
  tags: ["supplements", "his", "optimization", "pre-cycle", "evidence-based"],
  title: "Zinc",
  icon: "💊",
  body: {
    name: "Zinc",
    dosageRange: "25-50mg daily",
    dailyTiming:
      "Take with food to reduce nausea. Do not take at the same time as high-dose iron or calcium.",
    timing: "Start 3 months before planned conception",
    bioavailability: {
      form: "Zinc picolinate, citrate, and gluconate are commonly used supplemental forms.",
      absorption:
        "Take with food if it causes nausea, but avoid taking it at the same time as high-dose iron or calcium.",
      metabolism: "Long-term high-dose zinc can lower copper status.",
      cautions: [
        "Do not stack multiple zinc-containing products above the recommended range without monitoring.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "him",
    foods: ["Oysters", "Red meat", "Pumpkin seeds", "Chickpeas"],
    expectations: {
      canExpect: [
        "Supports healthy sperm count and concentration",
        "Essential for testosterone production",
        "May improve sperm motility and morphology",
      ],
      shouldNotExpect: [
        "Allow 3 months - sperm takes ~72 days to fully mature",
        "Won't resolve severe male factor infertility on its own",
        "If zinc levels are already normal, extra supplementation has little benefit",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Zinc",
        url: "https://www.amazon.com/s?k=zinc+30mg+supplement",
      },
      { label: "iHerb - Zinc", url: "https://www.iherb.com/c/zinc" },
    ],
  },
  sources: [
    {
      title:
        "Zinc levels in seminal plasma and their correlation with male infertility: A systematic review and meta-analysis",
      authors: "Zhao J et al.",
      journal: "Scientific Reports",
      year: 2016,
      url: "https://pubmed.ncbi.nlm.nih.gov/26932683/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "The Effect of Dietary Supplements on Male Infertility in Terms of Pregnancy, Live Birth, and Sperm Parameters: A Systematic Review and Meta-Analysis",
      authors: "Michaelsen MP et al.",
      journal: "Nutrients",
      year: 2025,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12113742/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const lCarnitine: ContentBlock<"supplement"> = {
  id: makeId("supplement-l-carnitine"),
  kind: "supplement",
  tags: ["supplements", "his", "optimization", "pre-cycle", "evidence-based"],
  title: "L-Carnitine",
  icon: "💊",
  body: {
    name: "L-Carnitine",
    dosageRange: "2-3g daily",
    dailyTiming:
      "Split gram-level doses across the day, ideally with meals, to improve tolerance.",
    timing: "Start 3 months before planned conception",
    bioavailability: {
      form: "Semen studies use L-carnitine, acetyl-L-carnitine, or combinations; they are not interchangeable on a milligram-for-milligram basis.",
      absorption: "Split gram-level doses across the day to improve tolerance.",
      metabolism:
        "Gut bacteria can metabolize carnitine into TMAO, which may matter for cardiovascular-risk discussions.",
    },
    evidenceStrength: "strong",
    forWhom: "him",
    foods: ["Red meat", "Chicken", "Milk", "Avocado"],
    expectations: {
      canExpect: [
        "Supports sperm motility - one of the most studied male fertility supplements",
        "Provides energy to developing sperm cells",
        "May improve sperm concentration and morphology",
      ],
      shouldNotExpect: [
        "Allow 3 months for full effect - improvements are gradual",
        "Won't resolve severe oligospermia on its own - consult a urologist",
        "High doses may cause mild GI discomfort - start lower and increase",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - L-Carnitine",
        url: "https://www.amazon.com/s?k=l-carnitine+supplement",
      },
      {
        label: "iHerb - L-Carnitine",
        url: "https://www.iherb.com/c/l-carnitine",
      },
    ],
  },
  sources: [
    {
      title:
        "The efficacy of combined l-carnitine and l-acetyl carnitine in men with idiopathic oligoasthenoteratozoospermia: A systematic review and meta-analysis",
      authors: "Zhang X et al.",
      journal: "Andrologia",
      year: 2020,
      url: "https://pubmed.ncbi.nlm.nih.gov/31701550/",
      evidenceLevel: "meta-analysis",
    },
    {
      title: "Antioxidants for male subfertility",
      authors:
        "Smits RM, Mackenzie-Proctor R, Yazdani A, Stankiewicz MT, Jordan V, Showell MG",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2019,
      url: "https://pubmed.ncbi.nlm.nih.gov/30866036/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const selenium: ContentBlock<"supplement"> = {
  id: makeId("supplement-selenium"),
  kind: "supplement",
  tags: ["supplements", "his", "optimization", "pre-cycle"],
  title: "Selenium",
  icon: "💊",
  body: {
    name: "Selenium",
    dosageRange: "55-200mcg daily",
    dailyTiming:
      "Take once daily with food. Avoid stacking multiple selenium sources because the safety window is narrow.",
    timing: "Start 3 months before planned conception",
    bioavailability: {
      form: "Selenomethionine is commonly used and well absorbed; Brazil nuts are variable and can overshoot.",
      absorption:
        "Selenium is generally well absorbed, so dose control matters more than aggressive absorption tricks.",
      cautions: [
        "Selenium has a narrow safety window. Avoid multiple high-dose selenium sources.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "him",
    foods: ["Brazil nuts", "Tuna", "Eggs", "Sunflower seeds"],
    expectations: {
      canExpect: [
        "Antioxidant involved in protecting sperm DNA from oxidative damage",
        "May improve sperm motility when combined with Vitamin E",
        "Supports overall reproductive health",
      ],
      shouldNotExpect: [
        "Never mega-dose - excess selenium is toxic. Stay within the recommended range",
        "Won't treat infertility on its own - best as part of a broader protocol",
        "Results take months of consistent intake - no quick fixes",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Selenium",
        url: "https://www.amazon.com/s?k=selenium+200mcg",
      },
      {
        label: "iHerb - Selenium",
        url: "https://www.iherb.com/c/selenium",
      },
    ],
  },
  sources: [
    {
      title:
        "Impact of selenium and selenoproteins on idiopathic male infertility: a comprehensive review",
      authors: "Zečević N et al.",
      journal: "Frontiers in Nutrition",
      year: 2026,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12823510/",
      evidenceLevel: "review",
    },
    {
      title:
        "Efficacy of Selenium and/or N-Acetyl-Cysteine for Improving Semen Parameters in Infertile Men: A Double-Blind, Placebo Controlled, Randomized Study",
      authors: "Safarinejad MR, Safarinejad S",
      journal: "Journal of Urology",
      year: 2009,
      url: "https://pubmed.ncbi.nlm.nih.gov/19091331/",
      evidenceLevel: "rct",
    },
  ],
};

// --- Her additional supplements --------------------------------------

const myoInositol: ContentBlock<"supplement"> = {
  id: makeId("supplement-myo-inositol"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "Myo-Inositol",
  icon: "💊",
  body: {
    name: "Myo-Inositol",
    dosageRange: "2-4g daily (often combined with D-chiro-inositol 40:1 ratio)",
    dailyTiming:
      "Split the dose morning and evening. Powder or capsules are fine if you can take the full daily amount consistently.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "Many PMOS (ex PCOS) formulas use myo-inositol with D-chiro-inositol in a 40:1 ratio.",
      absorption:
        "Powder and capsules can both work; the main issue is reaching gram-level daily dosing consistently.",
      timing:
        "Splitting the dose morning and evening can improve GI tolerance and routine adherence.",
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Citrus fruits", "Beans", "Brown rice", "Cantaloupe"],
    expectations: {
      canExpect: [
        "Improves ovulation in PMOS (ex PCOS) - one of the best-studied supplements for this",
        "May improve egg quality and insulin sensitivity",
        "Supports regular menstrual cycles",
      ],
      shouldNotExpect: [
        "Doesn't replace medical PMOS (ex PCOS) management - use alongside your treatment plan",
        "If you ovulate regularly without PMOS (ex PCOS), the benefit is minimal",
        "Allow 2-3 months for cycle regulation - changes are gradual",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Myo-Inositol",
        url: "https://www.amazon.com/s?k=myo+inositol+supplement",
      },
      {
        label: "iHerb - Inositol",
        url: "https://www.iherb.com/c/inositol",
      },
    ],
    interactions: [
      {
        with: "Metformin",
        severity: "caution",
        concern:
          "Both improve insulin sensitivity through similar mechanisms. Combined use can amplify glucose-lowering effect.",
        recommendation:
          "Mention to your clinic so they can decide whether to keep both, lower a dose, or rely on one.",
        linkedBlockId: makeId("med-metformin"),
      },
      {
        with: "Lithium",
        severity: "critical",
        concern:
          "Inositol can interfere with lithium's mechanism of action and reduce its effectiveness for mood stabilization.",
        recommendation:
          "Do not combine without explicit sign-off from your prescribing psychiatrist.",
      },
    ],
  },
  sources: [
    {
      title:
        "Inositol is an effective and safe treatment in polycystic ovary syndrome: a systematic review and meta-analysis of randomized controlled trials",
      authors: "Greff D et al.",
      journal: "Reproductive Biology and Endocrinology",
      year: 2023,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9878965/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const vitaminC: ContentBlock<"supplement"> = {
  id: makeId("supplement-vitamin-c"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Vitamin C",
  icon: "💊",
  body: {
    name: "Vitamin C",
    dosageRange: "500-1,000mg daily",
    dailyTiming:
      "Take with or without food. Take with iron or plant-based iron meals if the goal is improving non-heme iron absorption.",
    timing: "Start at least 1 month before conception attempt",
    bioavailability: {
      form: "Standard ascorbic acid is usually adequate; buffered forms may be gentler for reflux-prone users.",
      absorption:
        "Absorption saturates at higher doses, so very large single doses are mostly excreted.",
      timing:
        "Can improve non-heme iron absorption when taken with plant-based iron or iron supplements.",
    },
    evidenceStrength: "moderate",
    forWhom: "both",
    foods: ["Bell peppers", "Strawberries", "Citrus fruits", "Kiwi"],
    expectations: {
      canExpect: [
        "Antioxidant protection for both egg and sperm DNA",
        "May reduce sperm DNA fragmentation in men",
        "May support progesterone in women with luteal phase defect (limited evidence)",
      ],
      shouldNotExpect: [
        "Unlikely to dramatically improve fertility on its own",
        "Doses above 1,000mg are simply excreted - more is not better",
        "Doesn't substitute for treating underlying fertility conditions",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Vitamin C",
        url: "https://www.amazon.com/s?k=vitamin+c+1000mg",
      },
      {
        label: "iHerb - Vitamin C",
        url: "https://www.iherb.com/c/vitamin-c",
      },
    ],
  },
  sources: [
    {
      title:
        "Antioxidant Supplementation on Sperm DNA Fragmentation and Sperm Parameters: A Systematic Review and Meta-Analysis",
      authors: "Noegroho BS et al.",
      journal: "Turkish Journal of Urology",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/36197144/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Antioxidant Supplementation on Male Fertility—A Systematic Review",
      authors: "Dimitriadis F, Borgmann H, Struck JP, Salem J, Kuru TH",
      journal: "Antioxidants (Basel)",
      year: 2023,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10135082/",
      evidenceLevel: "review",
    },
  ],
};

// --- Her additional supplements (continued) -------------------------

const dhea: ContentBlock<"supplement"> = {
  id: makeId("supplement-dhea"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "DHEA",
  icon: "💊",
  body: {
    name: "DHEA (Dehydroepiandrosterone)",
    dosageRange: "25-75mg daily (typically 25mg 3x/day)",
    dailyTiming:
      "Use only on your clinic's schedule. Clinics often split dosing across the day and monitor DHEA-S and testosterone.",
    timing:
      "Start 6-8 weeks before planned cycle - requires medical supervision",
    bioavailability: {
      form: "This is a hormone precursor, not a vitamin; product potency and actual dose matter.",
      metabolism:
        "DHEA can convert downstream into androgens and estrogens, so response varies by baseline hormone levels.",
      timing:
        "Clinics often split dosing across the day and monitor DHEA-S and testosterone.",
      cautions: [
        "Avoid self-starting if you have PMOS (ex PCOS), high androgens, acne, hair growth, or hormone-sensitive conditions.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "May improve ovarian response in women with diminished ovarian reserve",
        "Can increase egg yield during IVF stimulation",
        "Studies show improved pregnancy rates in poor responders",
      ],
      shouldNotExpect: [
        "Only beneficial with a diagnosis of diminished ovarian reserve",
        "This is a hormone, not a vitamin - requires medical supervision",
        "Your testosterone and DHEA-S levels must be monitored during use",
        "Evidence has become more mixed - the 2024 Cochrane review found DHEA likely makes little to no difference to live birth, while testosterone may help poor responders",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - DHEA",
        url: "https://www.amazon.com/s?k=dhea+25mg",
      },
      {
        label: "iHerb - DHEA",
        url: "https://www.iherb.com/c/dhea",
      },
    ],
  },
  sources: [
    {
      title:
        "DHEA use to improve likelihood of IVF/ICSI success in patients with diminished ovarian reserve: A systematic review and meta-analysis",
      authors: "Schwarze JE et al.",
      journal: "JBRA Assisted Reproduction",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/30125071/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Androgens (dehydroepiandrosterone or testosterone) for women undergoing assisted reproduction",
      authors:
        "Naik S, Lepine S, Nagels HE, Siristatidis CS, Kroon B, McDowell S",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2024,
      url: "https://pubmed.ncbi.nlm.nih.gov/38837771/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const vitaminE: ContentBlock<"supplement"> = {
  id: makeId("supplement-vitamin-e"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Vitamin E",
  icon: "💊",
  body: {
    name: "Vitamin E",
    dosageRange: "200-400 IU daily",
    dailyTiming:
      "Take with a meal that contains fat. Avoid adding high-dose vitamin E near procedures unless your clinic agrees.",
    timing: "Start 2-3 months before planned cycle",
    bioavailability: {
      form: "Natural mixed tocopherols may differ from synthetic alpha-tocopherol products.",
      absorption:
        "Vitamin E is fat-soluble and absorbs best with a meal containing fat.",
      cautions: [
        "High-dose vitamin E can increase bleeding risk, especially around procedures or with anticoagulants.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "both",
    foods: ["Almonds", "Sunflower seeds", "Avocado", "Spinach"],
    expectations: {
      canExpect: [
        "Antioxidant involved in protecting egg and sperm cells from oxidative damage",
        "May improve endometrial thickness in women",
        "Synergistic with selenium for sperm health in men",
      ],
      shouldNotExpect: [
        "High doses can be harmful - stick to the recommended 200-400 IU range",
        "Won't treat fertility issues on its own - best as part of a broader protocol",
        "Most effective when combined with other lifestyle and dietary changes",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Vitamin E",
        url: "https://www.amazon.com/s?k=vitamin+e+400+iu",
      },
      {
        label: "iHerb - Vitamin E",
        url: "https://www.iherb.com/c/vitamin-e",
      },
    ],
  },
  sources: [
    {
      title:
        "The effect of oral vitamin E supplementation on infertile women: a systematic review and meta-analysis",
      authors: "Wu JH et al.",
      journal: "Clinical and Experimental Obstetrics & Gynecology",
      year: 2021,
      url: "https://www.imrpress.com/journal/CEOG/48/2/10.31083/j.ceog.2021.02.2207",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Effect of vitamin E on Semen Quality Parameters: A Meta-Analysis of a Randomized Controlled Trial",
      authors: "Wang R, Wang S, Song Y, Zhou H, Pan Y, Liu L, Liu X",
      journal: "Urology Journal",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/36029025/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const nac: ContentBlock<"supplement"> = {
  id: makeId("supplement-nac"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "evidence-based"],
  title: "NAC (N-Acetyl Cysteine)",
  icon: "💊",
  body: {
    name: "NAC (N-Acetyl Cysteine)",
    dosageRange: "600-1,200mg daily",
    dailyTiming:
      "Can be taken with or without food. Take with food if it causes nausea.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "NAC supplies cysteine, a building block for glutathione, rather than acting as glutathione itself.",
      absorption:
        "Can be taken with or without food; food may reduce nausea for sensitive stomachs.",
      metabolism:
        "Works through antioxidant and mucus-thinning pathways, which is different from direct hormone treatment.",
    },
    evidenceStrength: "moderate",
    forWhom: "her",
    foods: ["Chicken", "Turkey", "Garlic", "Onions", "Cruciferous vegetables"],
    expectations: {
      canExpect: [
        "Provides cysteine, a building block for glutathione",
        "May improve ovulation rates in women with PMOS (ex PCOS)",
        "Supports egg quality through oxidative stress reduction",
      ],
      shouldNotExpect: [
        "Doesn't replace prescribed PMOS (ex PCOS) medications like metformin",
        "If you already ovulate normally, the benefit is minimal",
        "Antioxidant effects build gradually over months - no instant results",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - NAC",
        url: "https://www.amazon.com/s?k=nac+600mg",
      },
      {
        label: "iHerb - NAC",
        url: "https://www.iherb.com/c/n-acetyl-cysteine-nac",
      },
    ],
  },
  sources: [
    {
      title:
        "N-Acetylcysteine for Polycystic Ovary Syndrome: A Systematic Review and Meta-Analysis of Randomized Controlled Clinical Trials",
      authors: "Thakker D, Raval A, Patel I, Walia R",
      journal: "Obstetrics and Gynecology International",
      year: 2015,
      url: "https://pubmed.ncbi.nlm.nih.gov/25653680/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Effect of Antioxidants on Sperm Quality Parameters in Subfertile Men: A Systematic Review and Network Meta-Analysis of Randomized Controlled Trials",
      authors: "Su L, Qu H, Cao Y et al.",
      journal: "Advances in Nutrition",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/34694345/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const melatonin: ContentBlock<"supplement"> = {
  id: makeId("supplement-melatonin"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Melatonin",
  icon: "💊",
  body: {
    name: "Melatonin",
    dosageRange: "3mg daily (at bedtime)",
    dailyTiming:
      "Take close to bedtime and keep timing consistent. Avoid taking it during the day because it can cause sleepiness.",
    timing: "Start 1-2 months before IVF cycle",
    bioavailability: {
      form: "Immediate-release melatonin is usually used for bedtime dosing; extended-release products behave differently.",
      absorption:
        "Absorbs quickly and can cause next-day grogginess in some people.",
      timing:
        "Take close to bedtime and keep timing consistent because light exposure and circadian rhythm matter.",
      cautions: [
        "Confirm whether to stop before transfer or pregnancy with your clinic.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "her",
    foods: ["Tart cherries", "Pistachios", "Milk", "Goji berries"],
    expectations: {
      canExpect: [
        "An antioxidant that has been measured in follicular fluid",
        "May improve egg quality during IVF stimulation",
        "May support sleep timing, which can affect reproductive hormones",
      ],
      shouldNotExpect: [
        "Evidence is mostly specific to IVF - not a general fertility supplement",
        "Higher doses are not better - 3mg is the studied effective dose",
        "Discuss with your doctor before continuing use during pregnancy",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Melatonin",
        url: "https://www.amazon.com/s?k=melatonin+3mg",
      },
      {
        label: "iHerb - Melatonin",
        url: "https://www.iherb.com/c/melatonin",
      },
    ],
  },
  sources: [
    {
      title:
        "Melatonin Application in Assisted Reproductive Technology: A Systematic Review and Meta-Analysis of Randomized Trials",
      authors: "Hu KL, Ye X, Wang S, Zhang D",
      journal: "Frontiers in Endocrinology",
      year: 2020,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7118201/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Oxidative stress impairs oocyte quality and melatonin protects oocytes from free radical damage and improves fertilization rate",
      authors:
        "Tamura H, Takasaki A, Miwa I, Taniguchi K, Maekawa R, Asada H, Taketani T, Matsuoka A, Yamagata Y, Shimamura K, Morioka H, Ishikawa H, Reiter RJ, Sugino N",
      journal: "Journal of Pineal Research",
      year: 2008,
      url: "https://pubmed.ncbi.nlm.nih.gov/18339123/",
      evidenceLevel: "rct",
    },
  ],
};

const iron: ContentBlock<"supplement"> = {
  id: makeId("supplement-iron"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Iron",
  icon: "💊",
  body: {
    name: "Iron (ferrous bisglycinate)",
    dosageRange: "18-27mg daily (or as directed by bloodwork)",
    dailyTiming:
      "Take away from calcium, coffee, tea, antacids, and thyroid medication. Vitamin C can improve absorption; food may reduce nausea but can lower absorption.",
    timing: "Only if deficient - test ferritin levels first",
    bioavailability: {
      form: "Ferrous bisglycinate is often gentler; ferrous sulfate is common but can cause more constipation.",
      absorption:
        "Vitamin C improves absorption. Calcium, tea, coffee, and antacids can reduce absorption.",
      timing:
        "Take away from calcium supplements, prenatal calcium, thyroid medication, and coffee or tea.",
      cautions: [
        "Only treat confirmed deficiency unless your clinician has another reason.",
      ],
    },
    evidenceStrength: "strong",
    forWhom: "her",
    foods: ["Red meat", "Spinach", "Lentils", "Dark chocolate"],
    expectations: {
      canExpect: [
        "Corrects iron-deficiency anemia - linked to ovulatory infertility",
        "Supports healthy blood flow to uterus and developing follicles",
        "Ferrous bisglycinate form is gentler on the stomach",
      ],
      shouldNotExpect: [
        "Only supplement if blood tests confirm deficiency - excess iron is harmful",
        "Won't help fertility if your iron levels are already normal",
        "Replenishing iron stores takes 3-6 months - be patient",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Iron",
        url: "https://www.amazon.com/s?k=ferrous+bisglycinate+iron",
      },
      {
        label: "iHerb - Iron",
        url: "https://www.iherb.com/c/iron",
      },
    ],
  },
  sources: [
    {
      title: "Iron intake and risk of ovulatory infertility",
      authors: "Chavarro JE, Rich-Edwards JW, Rosner BA, Willett WC",
      journal: "Obstetrics & Gynecology",
      year: 2006,
      url: "https://pubmed.ncbi.nlm.nih.gov/17077236/",
      evidenceLevel: "cohort",
    },
  ],
};

const probiotics: ContentBlock<"supplement"> = {
  id: makeId("supplement-probiotics"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Probiotics",
  icon: "💊",
  body: {
    name: "Probiotics (Lactobacillus strains)",
    dosageRange: "10-50 billion CFU daily",
    dailyTiming:
      "Follow the product label because strains differ. Take at a consistent time and check whether the product needs refrigeration.",
    timing: "Start at least 1 month before planned conception",
    bioavailability: {
      form: "Strain selection matters more than total CFU; fertility and vaginal-microbiome data focus on Lactobacillus-dominant products.",
      absorption:
        "Probiotics do not work like absorbed nutrients. Survival through storage and stomach acid matters.",
      cautions: [
        "Avoid assuming any generic probiotic will produce the same effect as studied strains.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "both",
    foods: ["Yogurt", "Kefir", "Sauerkraut", "Kimchi"],
    expectations: {
      canExpect: [
        "A Lactobacillus-dominant vaginal microbiome is associated with better IVF implantation in observational studies",
        "Supports gut health and nutrient absorption",
      ],
      shouldNotExpect: [
        "Oral probiotics have not been shown in RCTs to improve IVF outcomes - the microbiome-implantation link comes from observational data, not from supplementation trials",
        "Unlikely to dramatically improve fertility on its own",
        "Not all strains are equal - look for Lactobacillus-dominant formulas specifically",
        "Not a treatment for active infections - see your doctor for those",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Probiotics",
        url: "https://www.amazon.com/s?k=lactobacillus+probiotic+women",
      },
      {
        label: "iHerb - Probiotics",
        url: "https://www.iherb.com/c/probiotics",
      },
    ],
  },
  sources: [
    {
      title:
        "The vaginal microbiome as a predictor for outcome of in vitro fertilization with or without intracytoplasmic sperm injection: a prospective study",
      authors: "Koedooder R et al.",
      journal: "Human Reproduction",
      year: 2019,
      url: "https://pubmed.ncbi.nlm.nih.gov/31119299/",
      evidenceLevel: "cohort",
    },
    {
      title:
        "Longitudinal analysis of vaginal microbiota during IVF fresh embryo transfer and in early pregnancy",
      authors: "Väinämö S, Saqib S, Kalliala I et al.",
      journal: "Microbiology Spectrum",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37882794/",
      evidenceLevel: "cohort",
    },
  ],
};

const magnesium: ContentBlock<"supplement"> = {
  id: makeId("supplement-magnesium"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Magnesium",
  icon: "💊",
  body: {
    name: "Magnesium (glycinate or citrate)",
    dosageRange: "200-400mg daily",
    dailyTiming:
      "Evening dosing is common, especially for glycinate. Split the dose if it causes loose stools, and separate from thyroid medication or certain antibiotics.",
    timing:
      "Start anytime - many adults get less magnesium from food than recommended",
    bioavailability: {
      form: "Magnesium glycinate is often gentler; citrate can loosen stools; oxide is poorly absorbed for many people.",
      absorption: "Split doses if 400mg at once causes diarrhea or cramping.",
      timing:
        "Separate from thyroid medication and some antibiotics because magnesium can reduce their absorption.",
    },
    evidenceStrength: "emerging",
    forWhom: "both",
    foods: ["Dark chocolate", "Almonds", "Spinach", "Pumpkin seeds"],
    expectations: {
      canExpect: [
        "Supports progesterone production and hormonal balance",
        "May reduce stress symptoms and improve sleep in some people",
        "May reduce menstrual cramps and PMS symptoms",
      ],
      shouldNotExpect: [
        "Supports overall hormonal health rather than directly treating fertility",
        "Not all forms are equal - choose glycinate or citrate for best absorption",
        "Avoid magnesium oxide - it's poorly absorbed and can cause GI issues",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Magnesium",
        url: "https://www.amazon.com/s?k=magnesium+glycinate+400mg",
      },
      {
        label: "iHerb - Magnesium",
        url: "https://www.iherb.com/c/magnesium",
      },
    ],
  },
  sources: [
    {
      title:
        "Minerals and the Menstrual Cycle: Impacts on Ovulation and Endometrial Health",
      authors:
        "Kapper C, Oppelt P, Ganhör C, Gyunesh AA, Arbeithuber B, Stelzl P, Rezk-Füreder M",
      journal: "Nutrients",
      year: 2024,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11013220/",
      evidenceLevel: "review",
    },
    {
      title:
        "Oral magnesium supplementation for insomnia in older adults: a Systematic Review & Meta-Analysis",
      authors: "Mah J, Pitre T",
      journal: "BMC Complementary Medicine and Therapies",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/33865376/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

// --- Implantation & stress support -----------------------------------

const ashwagandha: ContentBlock<"supplement"> = {
  id: makeId("supplement-ashwagandha"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Ashwagandha",
  icon: "💊",
  body: {
    name: "Ashwagandha (KSM-66)",
    dosageRange: "300-600mg daily",
    dailyTiming:
      "Take with food. Evening dosing may be better if it feels sedating; morning dosing may be better if it feels activating.",
    timing:
      "Start anytime - stop before embryo transfer (discuss with your doctor)",
    bioavailability: {
      form: "Standardized root extracts such as KSM-66 or Sensoril are not equivalent to generic powder.",
      absorption: "Take with food if it causes GI upset or sedation.",
      metabolism:
        "Extracts can affect thyroid markers and sedating medications in some people.",
      cautions: [
        "Avoid during pregnancy and confirm stop timing before transfer or medicated cycles.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "both",
    foods: [],
    expectations: {
      canExpect: [
        "Clinically shown to lower cortisol and reduce perceived stress",
        "May improve sperm count and motility in men under stress",
        "Supports thyroid function - important for hormonal balance",
      ],
      shouldNotExpect: [
        "Must be stopped before embryo transfer - it may affect implantation",
        "Not safe during pregnancy - use only in the pre-conception phase",
        "KSM-66 is the most studied form - other extracts may differ in potency",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Ashwagandha",
        url: "https://www.amazon.com/s?k=ashwagandha+ksm-66",
      },
      {
        label: "iHerb - Ashwagandha",
        url: "https://www.iherb.com/c/ashwagandha",
      },
    ],
  },
  sources: [
    {
      title:
        "A Prospective, Randomized Double-Blind, Placebo-Controlled Study of Safety and Efficacy of a High-Concentration Full-Spectrum Extract of Ashwagandha Root in Reducing Stress and Anxiety in Adults",
      authors: "Chandrasekhar K, Kapoor J, Anishetty S",
      journal: "Indian Journal of Psychological Medicine",
      year: 2012,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3573577/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Clinical Evaluation of the Spermatogenic Activity of the Root Extract of Ashwagandha (Withania somnifera) in Oligospermic Males: A Pilot Study",
      authors:
        "Ambiye VR, Langade D, Dongre S, Aptikar P, Kulkarni M, Dongre A",
      journal: "Evidence-Based Complementary and Alternative Medicine",
      year: 2013,
      url: "https://pubmed.ncbi.nlm.nih.gov/24371462/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Efficacy and Safety of Ashwagandha Root Extract in Subclinical Hypothyroid Patients: A Double-Blind, Randomized Placebo-Controlled Trial",
      authors: "Sharma AK, Basu I, Singh S",
      journal: "Journal of Alternative and Complementary Medicine",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/28829155/",
      evidenceLevel: "rct",
    },
  ],
};

const vitaminBComplex: ContentBlock<"supplement"> = {
  id: makeId("supplement-vitamin-b-complex"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Vitamin B Complex",
  icon: "💊",
  body: {
    name: "Vitamin B Complex",
    dosageRange: "B6: 25-50mg, B12: 500-1,000mcg daily",
    dailyTiming:
      "Take in the morning or earlier in the day, with food if it causes nausea.",
    timing: "Start at least 1 month before conception attempt",
    bioavailability: {
      form: "Methylated B12 and folate forms can be useful for some people, but they are not required for everyone.",
      absorption:
        "B vitamins are water-soluble, so daily consistency matters more than taking a large dose occasionally.",
      cautions: [
        "Watch total B6 across products because chronic high dosing can cause nerve symptoms.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "her",
    foods: ["Eggs", "Salmon", "Leafy greens", "Chickpeas"],
    expectations: {
      canExpect: [
        "B6 is involved in steroid-hormone metabolism, including progesterone pathways",
        "B12 supports energy, methylation, and nervous system function",
        "May help regulate cycles and reduce PMS symptoms",
      ],
      shouldNotExpect: [
        "B vitamins are water-soluble - they need consistent daily intake",
        "If your diet is already B-rich, supplementation may not add much",
        "High-dose B6 (over 100mg) can cause nerve tingling - stay within range",
        "Folate levels have not been shown to improve IVF/ART pregnancy rates in a recent meta-analysis (Paffoni 2022)",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - B Complex",
        url: "https://www.amazon.com/s?k=vitamin+b+complex+methylated",
      },
      {
        label: "iHerb - B Complex",
        url: "https://www.iherb.com/c/vitamin-b-complex",
      },
    ],
  },
  sources: [
    {
      title: "B vitamins, polycystic ovary syndrome, and fertility",
      authors: "Thornburgh S, Gaskins AJ",
      journal: "Current Opinion in Endocrinology, Diabetes and Obesity",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/36165609/",
      evidenceLevel: "review",
    },
    {
      title:
        "Folate Levels and Pregnancy Rate in Women Undergoing Assisted Reproductive Techniques: a Systematic Review and Meta-analysis",
      authors:
        "Paffoni A, Reschini M, Noli SA, Viganò P, Parazzini F, Somigliana E",
      journal: "Reproductive Sciences",
      year: 2022,
      url: "https://pubmed.ncbi.nlm.nih.gov/33533009/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const lArginine: ContentBlock<"supplement"> = {
  id: makeId("supplement-l-arginine"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "L-Arginine",
  icon: "💊",
  body: {
    name: "L-Arginine",
    dosageRange: "3-6g daily",
    dailyTiming:
      "Split gram-level doses across the day. Take with food if it causes GI discomfort.",
    timing: "Start 1-2 months before planned cycle",
    bioavailability: {
      form: "L-arginine is a nitric-oxide precursor; citrulline may raise arginine levels differently, so evidence should not be automatically swapped.",
      absorption:
        "Gram-level doses are often better tolerated when split across the day.",
      metabolism:
        "It feeds nitric-oxide pathways involved in blood-vessel dilation rather than directly thickening the lining.",
      cautions: [
        "Avoid self-adding if you take blood-pressure medication, nitrates, or have recurrent herpes outbreaks without clinician input.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "her",
    foods: ["Turkey", "Pumpkin seeds", "Chickpeas", "Lentils"],
    expectations: {
      canExpect: [
        "Improves blood flow to the uterus - may support endometrial thickness",
        "Precursor to nitric oxide, which relaxes blood vessels",
        "Some studies show improved IVF outcomes in poor responders",
      ],
      shouldNotExpect: [
        "Evidence is still emerging - fewer large trials than other supplements",
        "May cause GI discomfort at higher doses - start with 3g and increase",
        "Not a substitute for medical evaluation of thin endometrium",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - L-Arginine",
        url: "https://www.amazon.com/s?k=l-arginine+supplement",
      },
      {
        label: "iHerb - L-Arginine",
        url: "https://www.iherb.com/c/l-arginine",
      },
    ],
  },
  sources: [
    {
      title:
        "Adjuvant L-arginine treatment for in-vitro fertilization in poor responder patients",
      authors: "Battaglia C et al.",
      journal: "Human Reproduction",
      year: 1999,
      url: "https://pubmed.ncbi.nlm.nih.gov/10402369/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Endometrial growth and uterine blood flow: a pilot study for improving endometrial thickness in the patients with a thin endometrium",
      authors:
        "Takasaki A, Tamura H, Miwa I, Taketani T, Shimamura K, Sugino N",
      journal: "Fertility and Sterility",
      year: 2010,
      url: "https://pubmed.ncbi.nlm.nih.gov/19200982/",
      evidenceLevel: "observational",
    },
  ],
};

// --- Emerging / Newer supplements ----------------------------------

const creatine: ContentBlock<"supplement"> = {
  id: makeId("supplement-creatine"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Creatine",
  icon: "⚡",
  body: {
    name: "Creatine (Monohydrate)",
    dosageRange: "3-5g daily",
    dailyTiming:
      "Take once daily at any consistent time. Taking with a meal can make it easier to remember and may reduce stomach upset.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "Creatine monohydrate is the best-studied form; most specialty forms have weaker evidence.",
      absorption:
        "Daily consistency matters more than loading for this use case.",
      metabolism:
        "Creatine supports cellular energy buffering, but fertility-specific metabolism data are still early.",
    },
    evidenceStrength: "emerging",
    forWhom: "both",
    foods: ["Red meat", "Fish", "Poultry"],
    expectations: {
      canExpect: [
        "Supports mitochondrial energy production in eggs and sperm",
        "Higher follicular fluid creatine linked to better blastocyst formation",
        "May support menstrual regularity through improved cellular energy",
        "Well-studied for safety in general health contexts",
      ],
      shouldNotExpect: [
        "Fertility-specific RCTs are still lacking - current evidence is observational and animal-based",
        "General fitness dosing may differ from reproductive health dosing",
        "Some water retention is normal and not a concern",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Creatine Monohydrate",
        url: "https://www.amazon.com/s?k=creatine+monohydrate",
      },
    ],
  },
  sources: [
    {
      title:
        "Association between dietary intake of creatine and female reproductive health: Evidence from NHANES 2017-2020",
      authors: "Ostojic SM, Stea TH, Ellery SJ, Smith-Ryan AE",
      journal: "Food Science & Nutrition",
      year: 2024,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11266896/",
      evidenceLevel: "cohort",
    },
    {
      title:
        "Intratesticular creatine maintains spermatogenesis by defining tight junctions",
      authors: "Kuribayashi S et al.",
      journal: "Scientific Reports",
      year: 2024,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11680987/",
      evidenceLevel: "observational",
    },
    {
      title:
        "Creatine in women's health: bridging the gap from menstruation through pregnancy to menopause",
      authors: "Smith-Ryan AE et al.",
      journal: "Journal of the International Society of Sports Nutrition",
      year: 2025,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12086928/",
      evidenceLevel: "review",
    },
  ],
};

const alphaLipoicAcid: ContentBlock<"supplement"> = {
  id: makeId("supplement-alpha-lipoic-acid"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "pcos"],
  title: "Alpha-Lipoic Acid (ALA)",
  icon: "🛡️",
  body: {
    name: "Alpha-Lipoic Acid",
    dosageRange: "300-600mg daily",
    dailyTiming:
      "Often taken away from meals for absorption, but take with food if it causes nausea. Use caution if you take glucose-lowering medication or supplements.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "R-lipoic acid and stabilized forms may differ from standard alpha-lipoic acid, but fertility trials often use mixed products.",
      absorption:
        "Absorption can be higher away from meals, but food may improve tolerance.",
      metabolism:
        "Can lower blood glucose, which matters if combined with metformin, inositol, berberine, or diabetes medication.",
    },
    evidenceStrength: "emerging",
    forWhom: "her",
    foods: ["Spinach", "Broccoli", "Organ meats", "Tomatoes"],
    expectations: {
      canExpect: [
        "Potent antioxidant that protects eggs from oxidative damage",
        "May improve insulin sensitivity - particularly relevant for PMOS (ex PCOS)",
        "When combined with myo-inositol, may increase mature oocyte yield in IVF",
        "Reduces inflammatory markers through NF-κB pathway inhibition",
      ],
      shouldNotExpect: [
        "Most studies combine ALA with other supplements, making it hard to isolate its individual effect",
        "It is not a standalone PMOS (ex PCOS) treatment - it complements medical care",
        "May lower blood sugar, so monitor levels if you are on diabetes medication",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Alpha Lipoic Acid",
        url: "https://www.amazon.com/s?k=alpha+lipoic+acid+600mg",
      },
    ],
  },
  sources: [
    {
      title:
        "The role of alpha lipoic acid in female and male infertility: a systematic review",
      authors: "Di Tucci C et al.",
      journal: "Gynecological Endocrinology",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/33345661/",
      evidenceLevel: "review",
    },
    {
      title: "Alpha Lipoic Acid Efficacy in PCOS Treatment: What Is the Truth?",
      authors: "Guarano A, Capozzi A, Cristodoro M, Di Simone N, Lello S",
      journal: "Nutrients",
      year: 2023,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10386153/",
      evidenceLevel: "review",
    },
    {
      title:
        "Effect of oral alpha-lipoic acid (ALA) on sperm parameters: a systematic review and meta-analysis",
      authors: "Dong L, Yang F, Li J, Li Y, Yu X, Zhang X",
      journal: "Basic and Clinical Andrology",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9730623/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

const resveratrol: ContentBlock<"supplement"> = {
  id: makeId("supplement-resveratrol"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "age-factor"],
  title: "Resveratrol",
  icon: "🍇",
  body: {
    name: "Resveratrol",
    dosageRange: "100-200mg daily",
    dailyTiming:
      "Take with food for tolerance. Stop during the luteal phase, embryo transfer window, and pregnancy unless your clinician directs otherwise.",
    timing:
      "Start 3 months before cycle - stop during luteal phase and pregnancy",
    bioavailability: {
      form: "Trans-resveratrol is the form usually emphasized on labels, but human bioavailability is still limited.",
      absorption:
        "Resveratrol is rapidly metabolized, so high label dose does not equal high active tissue exposure.",
      metabolism:
        "It can influence estrogen signaling and decidualization pathways, which is why stop timing matters.",
      cautions: [
        "Do not continue through the luteal phase, embryo transfer, or pregnancy unless your clinician specifically directs it.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "her",
    foods: ["Red grapes", "Blueberries", "Dark chocolate", "Peanuts"],
    expectations: {
      canExpect: [
        "Activates SIRT1, a molecule linked to slowing ovarian aging",
        "Animal studies show improved oocyte quality, implantation, and live offspring rates in aging models",
        "Mechanistic plausibility for protecting against age-related fertility decline",
      ],
      shouldNotExpect: [
        "Human RCTs showing improved egg quality, fertilization, or live birth do not exist - the egg-quality case rests on mouse data and mechanism, not clinical trials",
        "Avoid use during the luteal phase - lab studies show resveratrol can impair endometrial decidualization, which is needed for implantation (Ochiai 2019)",
        "Avoid during pregnancy unless your clinician specifically recommends it",
        "This is not a proven anti-aging treatment - the evidence is still emerging",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Resveratrol",
        url: "https://www.amazon.com/s?k=resveratrol+supplement",
      },
    ],
  },
  sources: [
    {
      title: "Resveratrol and Female Fertility: A Systematic Review",
      authors: "Bertoldo A et al.",
      journal: "International Journal of Molecular Sciences",
      year: 2024,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11641680/",
      evidenceLevel: "review",
    },
    {
      title:
        "Short-term resveratrol treatment restored the quality of oocytes in aging mice",
      authors: "Okamoto N, Sato Y, Kawagoe Y, Shimizu T, Kawamura K",
      journal: "Aging (Albany NY)",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9365568/",
      evidenceLevel: "observational",
    },
    {
      title:
        "Resveratrol inhibits decidualization by accelerating downregulation of the CRABP2-RAR pathway in differentiating human endometrial stromal cells",
      authors:
        "Ochiai A, Kuroda K, Ozaki R, Ikemoto Y, Murakami K, Muter J, Matsumoto A, Itakura A, Brosens JJ, Takeda S",
      journal: "Cell Death & Disease",
      year: 2019,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6427032/",
      evidenceLevel: "observational",
    },
  ],
};

const curcumin: ContentBlock<"supplement"> = {
  id: makeId("supplement-curcumin"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle", "endo"],
  title: "Curcumin (Turmeric)",
  icon: "🟡",
  body: {
    name: "Curcumin / Turmeric Extract",
    dosageRange: "500-1,000mg daily (bioavailable form)",
    dailyTiming:
      "Take with food, especially a fat-containing meal, unless the product label says otherwise. Piperine-containing products have higher interaction risk.",
    timing:
      "Can start anytime - use a bioavailable formulation (with piperine or liposomal)",
    bioavailability: {
      form: "Plain curcumin has very low bioavailability; phytosome, liposomal, or piperine-containing products are designed to raise exposure.",
      absorption:
        "Take with food, especially fat-containing meals, unless the product label directs otherwise.",
      metabolism:
        "Piperine can change drug metabolism, so a 'more bioavailable' formula can also mean more interaction risk.",
      cautions: [
        "Use extra caution with anticoagulants, IVF medications, and surgery/procedure timing.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "both",
    foods: ["Turmeric root", "Curry powder"],
    expectations: {
      canExpect: [
        "Strong anti-inflammatory and antioxidant properties",
        "May help reduce endometriosis-related inflammation",
        "Some evidence for protecting sperm from oxidative damage",
        "Well-tolerated at standard doses",
      ],
      shouldNotExpect: [
        "Results on endometriosis pain in randomized trials have been mixed",
        "Curcumin has very low bioavailability - formulation matters a lot",
        "Effects on sperm are inconsistent across studies, with some showing negative results at high doses",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Curcumin",
        url: "https://www.amazon.com/s?k=curcumin+supplement+bioavailable",
      },
    ],
  },
  sources: [
    {
      title:
        "Effect of curcumin on painful symptoms of endometriosis: A triple-blind randomized controlled trial",
      authors: "Gudarzi R et al.",
      journal: "Phytotherapy Research",
      year: 2024,
      url: "https://pubmed.ncbi.nlm.nih.gov/37818734/",
      evidenceLevel: "rct",
    },
    {
      title: "Curcumin and Endometriosis",
      authors: "Vallée A, Lecarpentier Y",
      journal: "International Journal of Molecular Sciences",
      year: 2020,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7177778/",
      evidenceLevel: "review",
    },
  ],
};

const berberine: ContentBlock<"supplement"> = {
  id: makeId("supplement-berberine"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "pcos"],
  title: "Berberine",
  icon: "🌱",
  body: {
    name: "Berberine",
    dosageRange: "500mg twice daily",
    dailyTiming:
      "Often taken with meals to target post-meal glucose and reduce GI side effects. Do not combine casually with metformin or diabetes medication.",
    timing: "Start 3 months before planned cycle",
    bioavailability: {
      form: "Berberine has low oral bioavailability, but it can still have meaningful gut and metabolic effects.",
      absorption:
        "Often taken with meals to target post-meal glucose and reduce GI side effects.",
      metabolism:
        "Can affect CYP enzymes and P-gp transporters, so medication interactions are plausible.",
      cautions: [
        "Do not combine casually with metformin, diabetes medication, or pregnancy attempts without clinician guidance.",
      ],
    },
    evidenceStrength: "moderate",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "Improves insulin sensitivity through AMPK activation - comparable to metformin in some studies",
        "May restore regular ovulation in PMOS (ex PCOS) by reducing insulin resistance and androgen levels",
        "In PMOS (ex PCOS) women undergoing IVF, 3-month berberine pretreatment improved live birth rate vs metformin and reduced severe OHSS (An 2014)",
        "May have cardiovascular benefits beyond metformin (lipid profile improvement)",
      ],
      shouldNotExpect: [
        "It is not FDA-approved for PMOS (ex PCOS) - it is a botanical supplement, not a medication",
        "GI side effects (nausea, cramping) are common, especially at higher doses",
        "Should not be combined with metformin without medical supervision due to additive blood sugar lowering",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Berberine",
        url: "https://www.amazon.com/s?k=berberine+500mg",
      },
    ],
  },
  sources: [
    {
      title:
        "Berberine—A Promising Therapeutic Approach to Polycystic Ovary Syndrome in Infertile/Pregnant Women",
      authors: "Ionescu OM et al.",
      journal: "Life (Basel)",
      year: 2023,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9864590/",
      evidenceLevel: "review",
    },
    {
      title:
        "Study on the Effect of Berberine, Myoinositol, and Metformin in Women with Polycystic Ovary Syndrome: A Prospective Randomised Study",
      authors: "Mishra N, Verma R, Jadaun P",
      journal: "Cureus",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8890747/",
      evidenceLevel: "rct",
    },
    {
      title:
        "The use of berberine for women with polycystic ovary syndrome undergoing IVF treatment",
      authors: "An Y, Sun Z, Zhang Y, Liu B, Guan Y, Lu M",
      journal: "Clinical Endocrinology (Oxford)",
      year: 2014,
      url: "https://pubmed.ncbi.nlm.nih.gov/23869585/",
      evidenceLevel: "rct",
    },
    {
      title:
        "The Effect of Berberine on Reproduction and Metabolism in Women with Polycystic Ovary Syndrome: A Systematic Review and Meta-Analysis of Randomized Control Trials",
      authors: "Xie L, Zhang D, Ma H et al.",
      journal: "Evidence-Based Complementary and Alternative Medicine",
      year: 2019,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6930782/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

// --- Commonly marketed / evidence-caution supplements ----------------

const maca: ContentBlock<"supplement"> = {
  id: makeId("supplement-maca"),
  kind: "supplement",
  tags: ["supplements", "both-partners", "optimization", "pre-cycle"],
  title: "Maca",
  icon: "🌱",
  body: {
    name: "Maca root",
    dosageRange: "Commonly marketed at 1.5-3g daily",
    dailyTiming:
      "There is no proven fertility-specific best time. If used, take with food and avoid adding it during monitored cycles without telling your clinic.",
    timing:
      "If used, trial periods are usually 8-12 weeks; stop once pregnant unless your clinician advises otherwise",
    bioavailability: {
      form: "Gelatinized maca removes some starch and may be easier to digest, but it does not prove better fertility outcomes.",
      absorption:
        "Most claims are not based on a known absorbed fertility-active compound.",
      metabolism:
        "Marketing often implies hormone effects, but human endocrine effects are not reliably established.",
    },
    evidenceStrength: "mixed",
    forWhom: "both",
    foods: [],
    expectations: {
      canExpect: [
        "You may see it marketed for libido, energy, and sperm quality",
        "Small male-factor studies have looked at sperm concentration and motility",
        "It is best treated as a low-certainty adjunct, not a core fertility supplement",
      ],
      shouldNotExpect: [
        "Reliable improvement in IVF outcomes, egg quality, or pregnancy rates",
        "Consistent sperm benefits - systematic reviews describe mixed results",
        "A substitute for semen analysis, lifestyle changes, or urology evaluation",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Maca",
        url: "https://www.amazon.com/s?k=maca+root+supplement",
      },
      { label: "iHerb - Maca", url: "https://www.iherb.com/c/maca" },
    ],
  },
  sources: [
    {
      title:
        "Maca (Lepidium meyenii Walp.) on Semen Quality Parameters: A Systematic Review and Meta-Analysis",
      authors: "Lee HW, Lee MS, Qu F, Lee JW, Kim E",
      journal: "Frontiers in Pharmacology",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9468664/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Exploring the chemical and pharmacological variability of Lepidium meyenii: a comprehensive review of the effects of maca",
      authors:
        "Ulloa del Carpio N, Alvarado-Corella D, Quiñones-Laveriano DM, Araya-Sibaja A, Vega-Baudrit J, Monagas-Juan M, Navarro-Hoyos M, Villar-López M",
      journal: "Frontiers in Pharmacology",
      year: 2024,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10910417/",
      evidenceLevel: "review",
    },
  ],
};

const royalJelly: ContentBlock<"supplement"> = {
  id: makeId("supplement-royal-jelly"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Royal Jelly",
  icon: "🍯",
  body: {
    name: "Royal Jelly",
    dosageRange: "No fertility-specific evidence-based dose",
    dailyTiming:
      "There is no proven fertility-specific best time. Avoid if you are allergic to bee products, asthma-prone, or pregnant unless your clinician clears it.",
    timing:
      "Not routinely recommended; avoid if allergic to bee products, asthma-prone, or pregnant unless cleared by your clinician",
    bioavailability: {
      form: "Capsules, fresh royal jelly, and blends are not standardized to a fertility-relevant active dose.",
      absorption:
        "There is no validated absorption target that translates to egg-quality or IVF benefit.",
      cautions: [
        "Allergy risk is the key bioavailability-adjacent issue: an absorbed allergen can be clinically dangerous.",
      ],
    },
    evidenceStrength: "debunked",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "You may see claims that it improves egg quality because queen bees eat royal jelly",
        "Most supportive fertility data are animal studies or very small human studies",
        "This card is included so unsupported fertility claims can be evaluated in one place",
      ],
      shouldNotExpect: [
        "Reliable improvement in ovarian reserve, IVF response, egg quality, or live birth",
        "Human fertility benefits comparable to established supplements like folate or vitamin D",
        "Low allergy risk - royal jelly can trigger serious reactions in susceptible people",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Royal jelly",
        url: "https://www.amazon.com/s?k=royal+jelly+supplement",
      },
    ],
  },
  sources: [
    {
      title:
        "Royal Jelly Promotes Ovarian Follicles Growth and Increases Steroid Hormones in Immature Rats",
      authors: "Ghanbari E, Khazaei MR, Khazaei M, Nejati V",
      journal: "International Journal of Fertility & Sterility",
      year: 2017,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5641457/",
      evidenceLevel: "observational",
    },
    {
      title: "Case of anaphylaxis caused by ingestion of royal jelly",
      authors: "Katayama M, Aoki M, Kawana S",
      journal: "Journal of Dermatology",
      year: 2008,
      url: "https://pubmed.ncbi.nlm.nih.gov/18419679/",
      evidenceLevel: "observational",
    },
  ],
};

const shatavari: ContentBlock<"supplement"> = {
  id: makeId("supplement-shatavari"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Shatavari",
  icon: "🌿",
  body: {
    name: "Shatavari (Asparagus racemosus)",
    dosageRange: "No standardized fertility dose",
    dailyTiming:
      "There is no proven fertility-specific best time. Do not layer onto hormone medication without clinician guidance.",
    timing:
      "Discuss with your clinician before use, especially if you are on hormones, have estrogen-sensitive conditions, or are pregnant",
    bioavailability: {
      form: "Powders, extracts, and traditional preparations can differ substantially in concentration.",
      absorption:
        "There is no standardized fertility dose or validated absorbed marker to compare products.",
      metabolism:
        "Possible hormone-like effects are exactly why it should not be casually layered onto fertility medications.",
    },
    evidenceStrength: "mixed",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "You may see it marketed in Ayurveda for reproductive symptoms, cycles, and libido",
        "Some early studies explore PMOS (ex PCOS) or hormonal effects, but evidence quality is limited",
        "It may be more relevant as a cultural or complementary-care topic than a proven fertility tool",
      ],
      shouldNotExpect: [
        "Clinically proven improvement in ovulation, IVF outcomes, or live birth",
        "Predictable hormone effects - formulations and doses vary widely",
        "A replacement for evidence-based PMOS (ex PCOS), endometriosis, or infertility treatment",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Shatavari",
        url: "https://www.amazon.com/s?k=shatavari+supplement",
      },
    ],
  },
  sources: [
    {
      title:
        "Efficacy and safety of Shatavari root extract in women with Polycystic Ovarian Syndrome: a randomized, double-blind, placebo-controlled trial",
      authors: "Mhatre Y, Jadhav P, Malik A, Srivathsan M, Langade D",
      journal: "Frontiers in Endocrinology",
      year: 2026,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12971452/",
      evidenceLevel: "rct",
    },
    {
      title: "Shatavari (Asparagus racemosus): A Promising Ally for Fertility",
      authors: "Oyovwi MO, Chijiokwu EA, Ben-Azu B, Ugwuishi EW, Jeroh E",
      journal: "Current Nutrition Reports",
      year: 2025,
      url: "https://pubmed.ncbi.nlm.nih.gov/40974515/",
      evidenceLevel: "review",
    },
  ],
};

const vitex: ContentBlock<"supplement"> = {
  id: makeId("supplement-vitex"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle"],
  title: "Vitex",
  icon: "🌿",
  body: {
    name: "Vitex / Chasteberry",
    dosageRange: "No fertility-specific standard dose",
    dailyTiming:
      "There is no proven fertility-specific best time. Avoid self-adding during medicated cycles or pregnancy attempts without clinician guidance.",
    timing:
      "Do not combine with fertility hormones, dopamine-related medications, or pregnancy attempts without clinician guidance",
    bioavailability: {
      form: "Extracts are usually standardized differently across brands, so dose comparisons are messy.",
      metabolism:
        "Vitex may act through dopamine/prolactin signaling rather than direct ovarian nutrition.",
      timing:
        "This pathway makes timing risky during medicated cycles or when prolactin, luteal phase, or ovulation issues have not been evaluated.",
    },
    evidenceStrength: "mixed",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "You may see it marketed for PMS, luteal phase support, and cycle regulation",
        "Some data suggest prolactin-related or PMS benefits, but fertility-specific evidence is weak",
        "It may be discussed if cycles are irregular, but it should not be self-added during treatment cycles",
      ],
      shouldNotExpect: [
        "Reliable improvement in implantation, IVF outcomes, or live birth",
        "A harmless add-on during medicated cycles - it may interact with hormone signaling",
        "A substitute for checking prolactin, thyroid function, ovulation, or luteal phase concerns",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - Vitex",
        url: "https://www.amazon.com/s?k=vitex+chasteberry+supplement",
      },
    ],
  },
  sources: [
    {
      title:
        "Systematic Review of Premenstrual, Postmenstrual and Infertility Disorders of Vitex Agnus Castus",
      authors: "Rafieian-Kopaei M, Movahedi M",
      journal: "Electronic Physician",
      year: 2017,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5308513/",
      evidenceLevel: "review",
    },
    {
      title:
        "Does Vitex Agnus-Castus L. Have Deleterious Effect on Fertility and Pregnancy Outcome? An Experimental Study on Rats for Prediction of Its Safety",
      authors: "Najib FS et al.",
      journal: "Journal of Pharmacopuncture",
      year: 2022,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9240410/",
      evidenceLevel: "observational",
    },
  ],
};

const nadNmn: ContentBlock<"supplement"> = {
  id: makeId("supplement-nad-nmn"),
  kind: "supplement",
  tags: ["supplements", "her", "optimization", "pre-cycle", "age-factor"],
  title: "NAD+ / NMN",
  icon: "⚡",
  body: {
    name: "NAD+ precursors (NMN / NR)",
    dosageRange: "No fertility-specific evidence-based dose",
    dailyTiming:
      "There is no proven fertility-specific best time. If used, take consistently and avoid during pregnancy unless your clinician recommends it.",
    timing:
      "Research use is still evolving; avoid during pregnancy unless your clinician specifically recommends it",
    bioavailability: {
      form: "NMN and NR are different NAD+ precursors; animal oocyte data do not prove equivalent human fertility effects.",
      absorption:
        "Raising NAD-related metabolites does not automatically mean improved egg quality or live birth.",
      metabolism:
        "These compounds feed cellular NAD+ pathways, but human reproductive tissue exposure is not well defined.",
    },
    evidenceStrength: "emerging",
    forWhom: "her",
    foods: [],
    expectations: {
      canExpect: [
        "You may see it marketed for ovarian aging, mitochondrial function, and egg quality",
        "Animal studies suggest NAD+ repletion can improve oocyte quality with age",
        "Human IVF trials are emerging, but routine fertility use is not established",
      ],
      shouldNotExpect: [
        "Proven improvement in pregnancy or live birth rates",
        "Clear guidance on dose, product form, or long-term safety in fertility patients",
        "The same evidence level as CoQ10, vitamin D, folate, or PMOS (ex PCOS) inositol",
      ],
    },
    shopLinks: [
      {
        label: "Amazon - NMN",
        url: "https://www.amazon.com/s?k=nmn+supplement",
      },
      {
        label: "Amazon - NR",
        url: "https://www.amazon.com/s?k=nicotinamide+riboside",
      },
    ],
  },
  sources: [
    {
      title:
        "NAD+ Repletion Rescues Female Fertility during Reproductive Aging",
      authors: "Bertoldo MJ et al.",
      journal: "Cell Reports",
      year: 2020,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7063679/",
      evidenceLevel: "observational",
    },
    {
      title:
        "Investigate the Efficacy of Using NMN to Improve Embryo Development Capacity and IVF Success Rate in Patients Who Have Experienced IVF Failures",
      authors: "Sunkaky Medical Cooperation (sponsor)",
      journal: "ClinicalTrials.gov",
      year: 2024,
      url: "https://clinicaltrials.gov/study/NCT06629636",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "NMN supplementation as a strategy to improve oocyte quality: a systematic review and transcriptomic analysis",
      authors: "Noh H, Sen Gupta S, Seshadri S, Viñals Gonzalez X",
      journal: "Journal of Assisted Reproduction and Genetics",
      year: 2025,
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12831783/",
      evidenceLevel: "review",
    },
  ],
};

registerBlock(creatine);
registerBlock(alphaLipoicAcid);
registerBlock(resveratrol);
registerBlock(curcumin);
registerBlock(berberine);
registerBlock(maca);
registerBlock(royalJelly);
registerBlock(shatavari);
registerBlock(vitex);
registerBlock(nadNmn);
registerBlock(coq10);
registerBlock(vitaminD);
registerBlock(omega3);
registerBlock(methylfolate);
registerBlock(prenatal);
registerBlock(choline);
registerBlock(iodine);
registerBlock(calcium);
registerBlock(zinc);
registerBlock(lCarnitine);
registerBlock(selenium);
registerBlock(myoInositol);
registerBlock(vitaminC);
registerBlock(dhea);
registerBlock(vitaminE);
registerBlock(nac);
registerBlock(melatonin);
registerBlock(iron);
registerBlock(probiotics);
registerBlock(magnesium);
registerBlock(ashwagandha);
registerBlock(vitaminBComplex);
registerBlock(lArginine);

// --- Link supplements to egg quality optimization --------------------

registerRelationship({
  from: makeId("tip-egg-quality"),
  to: makeId("supplement-coq10"),
  type: "related-to",
  label: "Key supplement",
});

registerRelationship({
  from: makeId("tip-egg-quality"),
  to: makeId("supplement-vitamin-d"),
  type: "related-to",
  label: "Key supplement",
});

registerRelationship({
  from: makeId("tip-egg-quality"),
  to: makeId("supplement-omega-3"),
  type: "related-to",
  label: "Supporting supplement",
});

registerRelationship({
  from: makeId("tip-egg-quality"),
  to: makeId("supplement-methylfolate"),
  type: "related-to",
  label: "Essential supplement",
});
